// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { NextImage, Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentWithContextProps } from 'lib/component-props';
import NextLink from 'next/link';
import { useI18n } from 'next-localization';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import EEPlaceholder from 'components/Sections/EEPlaceholder';
import classnames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import useHelpers from 'hooks/helpers';
import { useDispatch } from 'react-redux';
import { useFetchFundDetailsDocumentsQuery } from '@/store/fundDetails/services/fundDetailsApi';
import { fundDetailsActions } from '@/store/fundDetails';

import { DOCUMENTS_FORMATS_MAP } from 'constants/documentsFormatMap';
import { standardFormat, standardFormatWithTime } from 'constants/dateFormats';
import { pushDataLayerEvent } from 'hooks/useDataLayer';

import styles from './FundDetailsBanner.module.scss';
import { FundDetailsResponse, ShareClasses } from 'model/FundDetails';
import graphqlClientFactory from 'lib/graphql-client-factory';
import { SdrLogoQueryDocument } from 'components/graphql/GraphQL-SDRLogo.dynamic.graphql';
import { usePageData } from 'lib/page-data/PageDataContext';
import AbrdnPlaceholders from 'components/Generic/SitecoreWrappers/AbrdnPlaceholders';

const hkRegionsList = ['zh-HK', 'en-HK'];

type FundDetailsData = FundDetailsResponse & {
  selectedShareClass: ShareClasses;
  shareClassByIsin: (id: string) => {
    name: string;
  };
  shareClassDropdownFields: {
    options: {
      label: string;
      value: string;
    }[];
    defaultValue: {
      label: string;
      value: string;
    };
  };
};

const FundDetailsBanner = ({
  rendering,
  sitecoreContext,
  context,
}: ComponentWithContextProps): JSX.Element => {
  const [shouldRender, setShouldRender] = useState(true);
  const [sdrImageURL, setSdrImageURL] = useState('');
  const router = useRouter();
  const { t } = useI18n();
  const { localizeDate, formatCurrency, sanitizeURL } = useHelpers();
  const isExperienceEditor = sitecoreContext.pageEditing;
  const dispatch = useDispatch();

  // funddetails initial request made in middleware /plugins/fundDetails.ts
  const { data, error } = usePageData('fundDetailsData') ?? {};

  // Assumes ISIN is always the last segment of the `path`
  const { isinCode } = data;

  const {
    language,
    site: { name },
    fundsConfiguration: {
      allFundsLink,
      countryInvestors: { countryCode, literatureAuthorization },
    },
  } = context;
  const isHKRegion = hkRegionsList.includes(language);

  const fundDetailsBannerData = useMemo(() => {
    if (!data) return {};

    const {
      name,
      fundSizeValue,
      fundSizeValueDate,
      fundHeroType,
      formJson,
      sdrLabel,
      shareclasses,
      correlationFundRangeID,
      fundRangeName,
      id,
      fundNameId,
      shareClassNameId,
      sfdrArticleClassificationId,
      isInvestmentTrustDisclaimerAvailable,
      isImportantInfoAvailable,
      availableFundDetailsTabs,
      assetClassId,
      assetClassName,
      subAssetClassName,
    } = data;

    const selectedShareClass =
      shareclasses.find(({ isin }) => isin === isinCode?.toUpperCase()) || shareclasses[0];
    const shareClassByIsin = (isin) => shareclasses.find((shareClass) => shareClass.isin === isin);
    const defaultOption = shareclasses.find(({ isin }) => isin === isinCode.toUpperCase());
    const shareClassDropdownFields = {
      options: shareclasses.map(({ isin, name }) => {
        return {
          label: name,
          value: isin,
          selected: isinCode === isin,
        };
      }),
      defaultValue: {
        label: defaultOption.name,
        value: defaultOption.isin,
        selected: true,
      },
    };

    return {
      name,
      fundSizeValue,
      fundSizeValueDate,
      fundHeroType,
      formJson,
      sdrLabel,
      shareclasses,
      selectedShareClass,
      correlationFundRangeID,
      fundRangeName,
      id,
      fundNameId,
      shareClassNameId,
      sfdrArticleClassificationId,
      shareClassDropdownFields,
      isInvestmentTrustDisclaimerAvailable,
      isImportantInfoAvailable,
      availableFundDetailsTabs,
      assetClassId,
      shareClassByIsin,
      assetClassName,
      subAssetClassName,
    };
  }, [data, isinCode]) as FundDetailsData;

  const documentsPayload = useMemo(() => {
    if (!fundDetailsBannerData) return {};
    return {
      countryCode: countryCode,
      language: language,
      shareClass: fundDetailsBannerData.selectedShareClass?.id,
      correlationFundRangeId: fundDetailsBannerData.correlationFundRangeID,
      fundRangeName: fundDetailsBannerData.fundRangeName,
      literatureAuthorizations: [literatureAuthorization],
      fundName: fundDetailsBannerData.name,
      fund: fundDetailsBannerData.id,
      fundNameId: fundDetailsBannerData.fundNameId,
      shareClassNameId: fundDetailsBannerData.shareClassNameId,
      sfdrClassificationId: fundDetailsBannerData.sfdrArticleClassificationId,
      site: name,
    };
  }, [countryCode, fundDetailsBannerData, language, literatureAuthorization, name]);
  const documentsData = useFetchFundDetailsDocumentsQuery(documentsPayload, {
    skip: isExperienceEditor || !data,
  });
  const documentsError = useMemo(() => {
    return documentsData.isError;
  }, [documentsData]);

  const documents = documentsData.data?.content;

  const getDocumentFormat = (rawFormat) => {
    return DOCUMENTS_FORMATS_MAP[rawFormat];
  };

  const hasDocuments = !!documentsError || !!documentsData?.data?.content?.length;

  const sharedPriceFields = (
    { changeValue, currentAsAt, priceCurrencyCode, pricePerUnit },
    currencyCode
  ) => {
    return [
      {
        label: t('fundDetailsDailyPrice'),
        value: formatCurrency(pricePerUnit, currencyCode),
        date: localizeDate(currentAsAt, standardFormat),
      },
      {
        label: t('fundDetailsPriceChange'),
        value: formatCurrency(changeValue, currencyCode),
        date: localizeDate(currentAsAt, standardFormat),
      },
      {
        label: t('fundSize'),
        value:
          fundDetailsBannerData.fundSizeValue &&
          formatCurrency(fundDetailsBannerData.fundSizeValue, currencyCode, 'compact'),
        date: localizeDate(fundDetailsBannerData.fundSizeValueDate, standardFormat),
      },
      {
        label: t('fundDetailsCurrency'),
        value: priceCurrencyCode,
      },
    ];
  };

  const sharedInvTrustFields = ({ cefnav, exchangePrice, cefPremiumDiscount, exchangeDate }) => {
    return [
      {
        label: t('fundDetailsNav'),
        value: formatCurrency(cefnav),
        date: localizeDate(exchangeDate, standardFormat),
      },
      {
        label: t('fundDetailsMarketPrice'),
        value: formatCurrency(exchangePrice),
        date: localizeDate(exchangeDate, standardFormat),
      },
      {
        label: t('fundDetailsPremiumOrDiscount'),
        value: formatCurrency(cefPremiumDiscount),
      },
    ];
  };

  const shareClassDataColumnsByHeroType = useMemo(() => {
    return {
      defaultType: (data) => {
        const { priceCurrencyCode } = data;
        return sharedPriceFields(data, priceCurrencyCode);
      },
      investmentTrust: (data) => {
        return sharedInvTrustFields(data);
      },
      investmentTrustWithUnadjustedNAV: (data) => {
        const { unadjustedNav } = data;
        return [
          ...sharedInvTrustFields(data),
          {
            label: t('fundDetailsUnadjustedNav'),
            value: formatCurrency(unadjustedNav),
          },
        ];
      },
      USMutualFund: (data, tickerSymbol) => {
        const { changePercentage, currentAsAt, priceCurrencyCode, pricePerUnit } = data;
        return [
          {
            label: t('fundDetailsTicker'),
            value: tickerSymbol,
          },
          {
            label: t('fundDetailsMarketPrice'),
            value: formatCurrency(pricePerUnit, priceCurrencyCode),
            date: localizeDate(currentAsAt, standardFormat),
          },
          {
            label: t('fundDetailsDailyPriceChangePercentage'),
            value: `${formatCurrency(changePercentage, priceCurrencyCode)}%`,
            date: localizeDate(currentAsAt, standardFormat),
          },
        ];
      },
      USETF: (data, medianBidAskSpreads, investmentTrustPrices) => {
        const { pricePerUnit, priceCurrencyCode, currentAsAt, currencyCode, changePercentage } =
          data;
        const timeStamp = medianBidAskSpreads?.timeStamp;
        const value = medianBidAskSpreads?.value;
        const exchangePrice = investmentTrustPrices?.exchangePrice;
        const exchangeDate = investmentTrustPrices?.exchangeDate;
        return [
          {
            label: t('fundDetailsMarketPrice'),
            value: formatCurrency(exchangePrice, priceCurrencyCode),
            date: localizeDate(exchangeDate, standardFormat),
          },
          {
            label: t('fundDetailsNav'),
            value: formatCurrency(pricePerUnit, priceCurrencyCode, 'compact'),
            date: localizeDate(currentAsAt, standardFormat),
          },
          {
            label: t('fundDetailsPriceChangePercentage'),
            value: formatCurrency(changePercentage, currencyCode),
            date: localizeDate(currentAsAt, standardFormat),
          },
          {
            label: t('fundDetailsMedianBidAskSpread'),
            value: value,
            date: localizeDate(timeStamp, standardFormat),
          },
        ];
      },
      AUActiveETF: (data, iopVs) => {
        const { priceCurrencyCode, pricePerUnit, currentAsAt } = data;
        const { timeStampInAET, value } = iopVs;
        return [
          {
            label: t('fundDetailsNavPriceAud'),
            value: formatCurrency(pricePerUnit, priceCurrencyCode),
            date: localizeDate(currentAsAt, standardFormat),
          },
          {
            label: t('fundDetailsInavPriceAud'),
            value: formatCurrency(value, priceCurrencyCode),
            date: `${localizeDate(timeStampInAET, standardFormatWithTime)} ${t('timezoneAet')}`,
          },
          {
            label: t('fundDetailsCurrency'),
            value: priceCurrencyCode,
          },
        ];
      },
      USPreciousMetalsETF: (data, investmentTrustPrices) => {
        const { currentAsAt, priceCurrencyCode, pricePerUnit, currencyCode, changePercentage } =
          data;
        const { exchangePrice, exchangeDate } = investmentTrustPrices;
        const AUMField = `${formatCurrency(
          fundDetailsBannerData.fundSizeValue,
          priceCurrencyCode,
          'compact'
        )} ${priceCurrencyCode}`;
        return [
          {
            label: t('fundDetailsMarketPrice'),
            value: formatCurrency(exchangePrice, priceCurrencyCode),
            date: localizeDate(exchangeDate, standardFormat),
          },
          {
            label: t('fundDetailsNav'),
            value: formatCurrency(pricePerUnit, priceCurrencyCode, 'compact'),
            date: localizeDate(currentAsAt, standardFormat),
          },
          {
            label: t('fundDetailsPriceChangePercentage'),
            value: formatCurrency(changePercentage, currencyCode),
            date: localizeDate(currentAsAt, standardFormat),
          },
          {
            label: t('fundDetailsAum'),
            value: AUMField,
            date: localizeDate(fundDetailsBannerData.fundSizeValueDate, standardFormat),
          },
        ];
      },
    };
    // eslint-disable-next-line
  }, [fundDetailsBannerData.fundSizeValue, fundDetailsBannerData.fundSizeValueDate]);

  const shareClassDataColumns = useMemo(() => {
    if (!fundDetailsBannerData.selectedShareClass) return;

    const [price] = fundDetailsBannerData.selectedShareClass?.prices || [{}];
    const [investmentTrustPrices] = fundDetailsBannerData.selectedShareClass
      ?.investmentTrustPrices || [{}];

    const priceExist = price && !!Object.keys(price).length;
    const invTrustPriceExist = investmentTrustPrices && !!Object.keys(investmentTrustPrices).length;

    if (!priceExist && !invTrustPriceExist) return [];
    const { tickerSymbol } = fundDetailsBannerData.selectedShareClass;

    switch (fundDetailsBannerData.fundHeroType) {
      case 0:
        return shareClassDataColumnsByHeroType.defaultType(price);
      case 1:
        return shareClassDataColumnsByHeroType.investmentTrust(investmentTrustPrices);
      case 2:
        return shareClassDataColumnsByHeroType.investmentTrustWithUnadjustedNAV(
          investmentTrustPrices
        );
      case 3:
        return shareClassDataColumnsByHeroType.USMutualFund(price, tickerSymbol);
      case 4:
        return shareClassDataColumnsByHeroType.USPreciousMetalsETF(price, investmentTrustPrices);
      case 5:
        const [medianBidAskSpreads] = fundDetailsBannerData.selectedShareClass
          .medianBidAskSpreads || [{}];
        return shareClassDataColumnsByHeroType.USETF(
          price,
          medianBidAskSpreads,
          investmentTrustPrices
        );
      case 6:
        const [iopVs] = fundDetailsBannerData.selectedShareClass.iopVs;
        return shareClassDataColumnsByHeroType.AUActiveETF(price, iopVs);
      default:
        return [];
    }
  }, [
    fundDetailsBannerData.fundHeroType,
    fundDetailsBannerData.selectedShareClass,
    shareClassDataColumnsByHeroType,
  ]);

  const getPathByIsin = (newIsin: string) => {
    const currentISIN = fundDetailsBannerData.selectedShareClass.isin.toLowerCase();
    return router.asPath.replace(currentISIN, newIsin);
  };

  const onShareClassDropdownChange = (isin) => {
    router.push(getPathByIsin(isin.value.toLowerCase()));
    dispatch(fundDetailsActions.setSelectedShareClassIsin(isin.value));
  };

  const onContactButtonClick = () => {
    const contactFormElement = document.getElementById('contact-form');
    if (!contactFormElement) return;
    const headerHeight = window?.getComputedStyle(document.body).getPropertyValue('--headerHeight');
    contactFormElement.focus();
    window.scrollTo({
      top: contactFormElement.offsetTop - headerHeight,
      behavior: 'smooth',
    });
  };

  const updateDataLayer = (item) => {
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      pushDataLayerEvent({
        event: 'file_download',
        file_url: item.documentUri.includes('https://')
          ? item.documentUri
          : `${origin}${item.documentUri}`,
        file_name: item.title,
      });
    }
  };

  useEffect(() => {
    if (isExperienceEditor) return;
    if (!isinCode) setShouldRender(false);
    dispatch(fundDetailsActions.setSelectedShareClassIsin(isinCode));
    dispatch(fundDetailsActions.setFundDetailsData(fundDetailsBannerData));
    const graphQLClient = graphqlClientFactory();

    if (fundDetailsBannerData.sdrLabel !== 0) {
      (async () => {
        const result: any = await graphQLClient.request(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          SdrLogoQueryDocument as any,
          {
            fundSettingsId: context.fundSettings.id,
            language: context.language.toLowerCase(),
          }
        );
        const imageUrlBySdrLabel =
          result.item.children.results[0].children.results.find(
            (image) => +image.type.value === fundDetailsBannerData.sdrLabel
          ).image.src ?? '';
        setSdrImageURL(imageUrlBySdrLabel);
      })();
    }
    // eslint-disable-next-line
  }, [fundDetailsBannerData, isinCode]);
  return (
    <>
      {isExperienceEditor && (
        <EEPlaceholder>
          <div>Fund Details Banner</div>
          <Placeholder rendering={rendering} name="abrdn-preliminary-info" />
        </EEPlaceholder>
      )}

      {shouldRender && (
        <section className="default-section-padding background-white">
          <div className="container">
            <NextLink href={allFundsLink} className="d-inline-flex align-items-center gap-1">
              <i className="bi bi-chevron-left icon" />
              <span className="text-link">{t('allFunds')}</span>
            </NextLink>

            {error && <AlertMessageBase type="error" title={t(error)} className="mt-3" />}

            {!error && (
              <>
                <div className="row mt-3">
                  <div className="col-12 col-md-8">
                    <h2 className="h1">{fundDetailsBannerData.name}</h2>
                  </div>
                  {fundDetailsBannerData.sdrLabel !== 0 && (
                    <NextImage
                      field={{ value: { src: sdrImageURL } }}
                      className={styles['sdr-logo']}
                      alt="SDR logo"
                      width={338}
                      height={83}
                    />
                  )}
                </div>

                {isHKRegion && (
                  <AbrdnPlaceholders
                    name="abrdn-preliminary-info"
                    rendering={rendering}
                    className="mt-4 mt-lg-5"
                  />
                )}

                {fundDetailsBannerData.shareclasses.length !== 0 &&
                  fundDetailsBannerData.selectedShareClass.name && (
                    <div className="row mt-4 mt-md-5">
                      <div className="col-12 col-md-6 col-lg-4">
                        <div
                          className={`${
                            fundDetailsBannerData.shareclasses.length > 1
                              ? 'form-label'
                              : 'section-header-small'
                          } mb-2`}
                        >
                          {t('fundDetailsShareClass')}
                        </div>
                        {fundDetailsBannerData.shareclasses.length > 1 ? (
                          <AbrdnSelect
                            options={fundDetailsBannerData.shareClassDropdownFields.options}
                            defaultValue={
                              fundDetailsBannerData.shareClassDropdownFields.defaultValue
                            }
                            onChange={onShareClassDropdownChange}
                          />
                        ) : fundDetailsBannerData.shareclasses.length === 1 ? (
                          <div className="field-text">
                            {fundDetailsBannerData.selectedShareClass.name}
                          </div>
                        ) : (
                          <div className="field-text">{t('noResultsMessage')}</div>
                        )}
                      </div>
                    </div>
                  )}

                {(hasDocuments || shareClassDataColumns) && (
                  <div
                    className={classnames('row', {
                      'mt-5': hasDocuments || shareClassDataColumns.length,
                    })}
                  >
                    {hasDocuments && (
                      <div className="col-12 col-lg-4">
                        {documentsError ? (
                          <AlertMessageBase type="error" title="ERROR" />
                        ) : (
                          <div className="d-flex flex-wrap gap-3">
                            {documents.map((item) => (
                              <a
                                key={item.id}
                                href={sanitizeURL(item.documentUri)}
                                target="_blank"
                                className={`d-inline-flex align-items-center gap-1 ${styles['document-link']}`}
                                onClick={updateDataLayer(item)}
                              >
                                <span className={`text-link ${styles.underline}`}>
                                  {item.title}
                                </span>
                                <i
                                  aria-hidden="true"
                                  className={`ms-1 bi-filetype-${getDocumentFormat(
                                    item.contentType
                                  )}`}
                                />
                                <span className="sr-only">{t('opensInNewWindow')}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {shareClassDataColumns && (
                      <div
                        className={classnames('col-12 col-lg-8', {
                          'ps-lg-4': hasDocuments,
                        })}
                      >
                        <div className="d-lg-flex">
                          {hasDocuments && (
                            <div className={` my-4 my-lg-0 me-lg-4 ${styles.divider}`} />
                          )}

                          <div className="w-100 d-flex flex-wrap mt-n4 ms-n4">
                            {shareClassDataColumns.map(({ label, value, date }) => {
                              if (value) {
                                return (
                                  <div
                                    key={label}
                                    className={`w-50 ${styles['w-md-25']} pt-4 ps-4`}
                                  >
                                    <div className="section-header-small text-break">{label}</div>
                                    <div className="h4 mt-1">{value}</div>
                                    {date && <div className="body-small mt-1">{date}</div>}
                                  </div>
                                );
                              }
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {fundDetailsBannerData.formJson && (
                  <div className="mt-5">
                    <AbrdnButton buttonType="primary" onClick={onContactButtonClick}>
                      {t('contactUsFundForm')}
                    </AbrdnButton>
                  </div>
                )}

                {fundDetailsBannerData.isInvestmentTrustDisclaimerAvailable && (
                  <div
                    className="py-4 px-2 px-md-3 px-lg-5 mt-5 background-digital-light-grey"
                    data-testid="FundDetailsHeroBannerDisclaimer"
                  >
                    <p className="body-small">{t('fundDetailsCefDisclaimer')}</p>
                  </div>
                )}

                {!isHKRegion && (
                  <Placeholder
                    name="abrdn-preliminary-info"
                    rendering={rendering}
                    className="mt-5 mt-lg-6"
                  />
                )}
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
};

export default FundDetailsBanner;
